import { UserIcon, BuildingOfficeIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocationFormProps } from "data/reduxInterfaces";
import { useAppSelector } from "hooks/hooks";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import OnboardingTour from "utils/tours/OnboardingTour";
import { z } from "zod";

const formSchema = z.object({
  category: z.string({ required_error: "Le type  est obligatoire." }),
});

const LocationUserType: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  handleForm,
  FormData,
}) => {
  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: "onSubmit",
  });
  const [ownerType, setOwnerType] = useState<string>("");

  const steps = [
    {
      selector: ".profile-owner-form ",
      content: "Choisissez votre type de profil . ",
    },
  ];

  const handleTypeClick = (type: string) => {
    setOwnerType(type);
    setValue("category", type);
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    const { ...result } = data;

    FormData(result);
  };

  useEffect(() => {
    if (handleForm.type === "chekLocationFormThirteenOwner") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const updatelocationStates = useAppSelector((state) => state.location.locationData);

  useEffect(() => {
    if (updatelocationStates) {
      setValue("category", updatelocationStates.category);
      setOwnerType(updatelocationStates.category || "");
    }
  }, [updatelocationStates]);

  return (
    <div className={className}>
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">👤 Type de profil</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Choisissez le type de profil qui correspond à votre situation
        </p>
        <div className="border-b-2 w-20 border-eventToBee mt-4 mx-auto"></div>
      </div>

      {/* Options de sélection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
        {/* Option Particulier */}
        <button
          onClick={() => handleTypeClick("particular")}
          className={`group relative p-8 rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${
            ownerType === "particular"
              ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20 shadow-md"
              : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
          }`}
        >
          <div className="flex flex-col items-center space-y-4">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
                ownerType === "particular"
                  ? "bg-eventToBee text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-600"
              }`}
            >
              <UserIcon className="w-8 h-8" />
            </div>

            <div className="text-center">
              <h3
                className={`text-lg font-semibold mb-2 ${
                  ownerType === "particular" ? "text-eventToBee" : "text-gray-900 dark:text-white"
                }`}
              >
                Particulier
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Vous louez votre propriété personnelle pour des événements privés
              </p>
            </div>

            {ownerType === "particular" && (
              <div className="absolute top-3 right-3">
                <div className="w-6 h-6 bg-eventToBee rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </button>

        {/* Option Entreprise */}
        <button
          onClick={() => handleTypeClick("company")}
          className={`group relative p-8 rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${
            ownerType === "company"
              ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20 shadow-md"
              : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
          }`}
        >
          <div className="flex flex-col items-center space-y-4">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
                ownerType === "company"
                  ? "bg-eventToBee text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-600"
              }`}
            >
              <BuildingOfficeIcon className="w-8 h-8" />
            </div>

            <div className="text-center">
              <h3
                className={`text-lg font-semibold mb-2 ${
                  ownerType === "company" ? "text-eventToBee" : "text-gray-900 dark:text-white"
                }`}
              >
                Entreprise
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Vous représentez une entreprise qui loue des espaces professionnels
              </p>
            </div>

            {ownerType === "company" && (
              <div className="absolute top-3 right-3">
                <div className="w-6 h-6 bg-eventToBee rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Message d'aide */}
      {ownerType && (
        <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg max-w-2xl mx-auto">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-sm text-green-700 dark:text-green-300">
              Parfait ! Vous avez sélectionné le profil {ownerType === "particular" ? "Particulier" : "Entreprise"}
              . Cette information nous aidera à personnaliser votre expérience.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {errors.category?.message && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg max-w-2xl mx-auto">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-red-700 dark:text-red-300">{errors.category?.message}</span>
            </div>
          </div>
        )}
      </form>

      <OnboardingTour steps={steps} page_name={"ProfileOwnerForm"}></OnboardingTour>
    </div>
  );
};

export default LocationUserType;
